/**
 * The undo/redo + mode chrome — ADR-006 §13.0.1 (a surface is a pure resolver
 * over state, owning nothing but ephemeral DOM) and ADR-007 §3.3 (the History
 * is authoritative over the undo surfaces; buttons resolve from it). This is
 * the reference shape for every future panel: it holds NO truth. Enablement,
 * the dirty mark, the active mode, and the announcement line all resolve FROM
 * the store on every publish — v2's `updateUI` inverted (the store publishes,
 * this subscribes). Disabled buttons say WHY (No-Trap: never a dead button).
 * The only writes are `store.undo/redo/setMode` — there is no other path.
 *
 * Honest tier ① (brief §8.1): it renders only what the History KNOWS —
 * can-undo, can-redo, dirtiness, the command's own description. Nothing is a
 * fabricated glyph or a stage prop.
 * Depends on: app store, shell vocabulary. DOM only, no engine.
 * Depended on by: main (the loader mounts it).
 */
import { SHELL_MODES, isShellMode } from '../session/shell-state.js';
import type { AppStore } from '../session/store.js';

export interface MountedChrome {
  /** Re-resolve the whole chrome from the store — called on every publish. */
  refresh(): void;
  /** Unsubscribe and remove the DOM. */
  dispose(): void;
}

function button(label: string, aria: string): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.textContent = label;
  el.setAttribute('aria-label', aria);
  el.style.cssText =
    'font: inherit; padding: 4px 10px; border-radius: 6px; cursor: pointer; ' +
    'background: var(--accent-soft); color: var(--accent-text); ' +
    'border: 1px solid var(--accent-border);';
  return el;
}

export function mountUndoChrome(root: HTMLElement, store: AppStore): MountedChrome {
  const bar = document.createElement('div');
  bar.id = 'shell-chrome';
  bar.setAttribute('role', 'toolbar');
  bar.setAttribute('aria-label', 'Shell');
  bar.style.cssText =
    'position: absolute; top: 16px; left: 16px; display: flex; flex-wrap: wrap; max-width: calc(100% - 32px); box-sizing: border-box; gap: 8px; align-items: center; ' +
    "font-family: 'Inter', -apple-system, 'Segoe UI', sans-serif; font-size: 12px; " +
    'padding: 8px 12px; border-radius: 10px; background: var(--identity-grad); ' +
    'border: 1px solid var(--accent-border); color: var(--text-1);';

  // Mode chips — the FSM's single value, made visible (exactly one active).
  const modeButtons = SHELL_MODES.map((mode) => {
    const chip = button(mode, `Switch to ${mode} mode`);
    chip.dataset.mode = mode;
    chip.addEventListener('click', () => {
      const value = chip.dataset.mode;
      if (value && isShellMode(value)) store.setMode(value);
    });
    bar.append(chip);
    return chip;
  });

  const divider = document.createElement('span');
  divider.style.cssText = 'width: 1px; height: 18px; background: var(--accent-border);';
  bar.append(divider);

  const undoBtn = button('↶ Undo', 'Undo the last edit');
  undoBtn.addEventListener('click', () => store.undo());
  const redoBtn = button('↷ Redo', 'Redo the next edit');
  redoBtn.addEventListener('click', () => store.redo());
  bar.append(undoBtn, redoBtn);

  // The dot is decorative; the STATE speaks through a visually-hidden status
  // element — an aria-label on an aria-hidden node is removed from the a11y
  // tree with it, leaving saved/unsaved conveyed by color alone (Codex P2,
  // #54 round 4). Visually hidden, never display:none — that would silence it.
  const dirtyDot = document.createElement('span');
  dirtyDot.setAttribute('aria-hidden', 'true');
  dirtyDot.style.cssText = 'width: 8px; height: 8px; border-radius: 50%; background: transparent;';
  const dirtyStatus = document.createElement('span');
  dirtyStatus.setAttribute('role', 'status');
  dirtyStatus.dataset.dirtyStatus = '';
  dirtyStatus.style.cssText =
    'position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap;';
  const announce = document.createElement('span');
  announce.setAttribute('role', 'status');
  announce.style.cssText = 'color: var(--text-3); max-width: 40ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;';
  // Selection resolves from the store like everything else (§13.2: selection
  // is app state in Document vocabulary) — the chrome shows it, owns nothing.
  const selectionEl = document.createElement('span');
  selectionEl.dataset.selection = '';
  selectionEl.style.cssText = 'color: var(--accent-text); max-width: 24ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;';
  bar.append(dirtyDot, dirtyStatus, selectionEl, announce);

  root.append(bar);

  const applyAffordance = (btn: HTMLButtonElement, ok: boolean, reason: string | undefined): void => {
    btn.disabled = !ok;
    btn.style.opacity = ok ? '1' : '0.4';
    btn.style.cursor = ok ? 'pointer' : 'not-allowed';
    // Disabled says why (No-Trap); enabled clears the tip.
    if (reason && !ok) btn.title = reason;
    else btn.removeAttribute('title');
  };

  const refresh = (): void => {
    const activeMode = store.getShell().mode;
    for (const chip of modeButtons) {
      const active = chip.dataset.mode === activeMode;
      chip.setAttribute('aria-pressed', String(active));
      chip.style.background = active ? 'var(--accent-soft-active)' : 'var(--accent-soft)';
      chip.style.borderColor = active ? 'var(--accent-text)' : 'var(--accent-border)';
    }
    const undo = store.canUndo();
    const redo = store.canRedo();
    applyAffordance(undoBtn, undo.ok, undo.reason);
    applyAffordance(redoBtn, redo.ok, redo.reason);

    const dirty = store.isDirty();
    dirtyDot.style.background = dirty ? 'var(--accent-text)' : 'transparent';
    dirtyStatus.textContent = dirty ? 'unsaved changes' : 'all changes saved';
    const selection = store.getShell().selection;
    selectionEl.textContent = selection.length > 0 ? `sel: ${selection.join(', ')}` : '';
    const last = store.lastAction();
    announce.textContent = last ? `last: ${last}` : '';
  };

  const unsubscribe = store.subscribe(refresh);
  refresh();

  return {
    refresh,
    dispose() {
      unsubscribe();
      bar.remove();
    },
  };
}
